// Timezone and locale constants for consistent formatting
export const LOCALE = 'ro-RO'
export const TIMEZONE = 'Europe/Bucharest'

export const romanianMonths = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie'
]

export interface TargetMonth {
  year: number
  month: number
  monthName: string
  folderName: string
}

export function getTargetMonthFolder(): TargetMonth {
  const now = new Date()
  const currentDay = now.getDate()
  const currentMonth = now.getMonth()
  const currentYear = now.getFullYear()

  let targetMonth: number
  let targetYear: number

  if (currentDay < 15) {
    // Before the 15th - go to last month
    if (currentMonth === 0) {
      // January - go to December of previous year
      targetMonth = 11
      targetYear = currentYear - 1
    } else {
      targetMonth = currentMonth - 1
      targetYear = currentYear
    }
  } else {
    // 15th or after - use current month
    targetMonth = currentMonth
    targetYear = currentYear
  }

  const monthName = romanianMonths[targetMonth]
  const folderName = `${targetYear}/${monthName}`

  return {
    year: targetYear,
    month: targetMonth,
    monthName,
    folderName
  }
}

export function getYearFolderName(year: number): string {
  return year.toString()
}

export function getMonthFolderName(monthIndex: number): string {
  return romanianMonths[monthIndex]
}

// ============================================
// Date Formatting Functions (Europe/Bucharest)
// ============================================

/**
 * Format date and time for display.
 * Example: "20 decembrie 2025, 14:30"
 */
export function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString(LOCALE, {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Format date only for display.
 * Example: "20 decembrie 2025"
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString(LOCALE, {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

/**
 * Format short date for compact displays.
 * Example: "20 dec. 2025"
 */
export function formatShortDate(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString(LOCALE, {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

/**
 * Format date and time compactly.
 * Example: "20 dec. 2025, 14:30"
 */
export function formatDateTimeCompact(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString(LOCALE, {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Format time only.
 * Example: "14:30"
 */
export function formatTime(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleTimeString(LOCALE, {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Get a Date object in Bucharest timezone for comparisons.
 * Returns the date parts as they would appear in Bucharest.
 */
function getBucharestDateParts(date: Date): { year: number; month: number; day: number } {
  const formatter = new Intl.DateTimeFormat(LOCALE, {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })
  const parts = formatter.formatToParts(date)
  return {
    year: parseInt(parts.find(p => p.type === 'year')?.value || '0'),
    month: parseInt(parts.find(p => p.type === 'month')?.value || '0'),
    day: parseInt(parts.find(p => p.type === 'day')?.value || '0')
  }
}

/**
 * Format for chat date separators.
 * Returns "Astăzi", "Ieri", or the full date with weekday.
 */
export function formatDateSeparator(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()

  const dateParts = getBucharestDateParts(date)
  const nowParts = getBucharestDateParts(now)

  // Check if same day in Bucharest timezone
  if (dateParts.year === nowParts.year &&
      dateParts.month === nowParts.month &&
      dateParts.day === nowParts.day) {
    return 'Astăzi'
  }

  // Check if yesterday in Bucharest timezone
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayParts = getBucharestDateParts(yesterday)

  if (dateParts.year === yesterdayParts.year &&
      dateParts.month === yesterdayParts.month &&
      dateParts.day === yesterdayParts.day) {
    return 'Ieri'
  }

  // Otherwise return full date with weekday
  const options: Intl.DateTimeFormatOptions = {
    timeZone: TIMEZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }
  if (dateParts.year !== nowParts.year) {
    options.year = 'numeric'
  }
  return date.toLocaleDateString(LOCALE, options)
}

/**
 * Format for conversation list timestamps.
 * Smart display: time for today, "Ieri" for yesterday, weekday for last 7 days, otherwise short date.
 */
export function formatMessageTime(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()

  const dateParts = getBucharestDateParts(date)
  const nowParts = getBucharestDateParts(now)

  // Same day - show time only
  if (dateParts.year === nowParts.year &&
      dateParts.month === nowParts.month &&
      dateParts.day === nowParts.day) {
    return formatTime(dateString)
  }

  // Yesterday
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayParts = getBucharestDateParts(yesterday)

  if (dateParts.year === yesterdayParts.year &&
      dateParts.month === yesterdayParts.month &&
      dateParts.day === yesterdayParts.day) {
    return 'Ieri'
  }

  // Within last 7 days - show weekday
  const sevenDaysAgo = new Date(now)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  if (date > sevenDaysAgo) {
    return date.toLocaleDateString(LOCALE, { timeZone: TIMEZONE, weekday: 'long' })
  }

  // Older - show short date
  return date.toLocaleDateString(LOCALE, { timeZone: TIMEZONE, day: 'numeric', month: 'short' })
}

/**
 * Format relative time (e.g., "Acum 5 minute").
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Acum'
  if (diffMins < 60) return `Acum ${diffMins} ${diffMins === 1 ? 'minut' : 'minute'}`
  if (diffHours < 24) return `Acum ${diffHours} ${diffHours === 1 ? 'oră' : 'ore'}`
  if (diffDays < 7) return `Acum ${diffDays} ${diffDays === 1 ? 'zi' : 'zile'}`

  return formatDate(dateString)
}

/**
 * Format for file display - shows date and time compactly.
 * Example: "15 ian 2024 · 14:32"
 */
export function formatFileDate(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const datePart = date.toLocaleDateString(LOCALE, {
    timeZone: TIMEZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
  const timePart = date.toLocaleTimeString(LOCALE, {
    timeZone: TIMEZONE,
    hour: '2-digit',
    minute: '2-digit'
  })
  return `${datePart} · ${timePart}`
}

/**
 * Format for file list - smart short date.
 * Today: "14:32", Other days: "15/01"
 */
export function formatFileShortDate(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()

  const dateParts = getBucharestDateParts(date)
  const nowParts = getBucharestDateParts(now)

  // Same day - show time only
  if (dateParts.year === nowParts.year &&
      dateParts.month === nowParts.month &&
      dateParts.day === nowParts.day) {
    return formatTime(dateString)
  }

  // Other days - show day/month
  return date.toLocaleDateString(LOCALE, {
    timeZone: TIMEZONE,
    day: '2-digit',
    month: '2-digit'
  })
}
